import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Badge, Flex, Text, Box, HStack, Select, Input,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import { FiFileText } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { herramientasApi, catalogosApi, Herramienta, CatalogoItem, abrirPdfEnNuevaPestana } from '../../../api/serviciosApi';
import FormularioHerramienta from './FormularioHerramienta';
import ModalMovimientos from './ModalMovimientos';
import ModalReportes from './ModalReportes';

const ESTADOS_FISICOS = ['Nuevo', 'Bueno', 'Regular', 'Malo', 'Dañado'];
const ESTADO_COLORS: Record<string, string> = {
  Nuevo: 'green', Bueno: 'teal', Regular: 'yellow', Malo: 'orange', Dañado: 'red',
};

const TablaHerramientas: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenMov, onOpen: onOpenMov, onClose: onCloseMov } = useDisclosure();
  const { isOpen: isOpenRep, onOpen: onOpenRep, onClose: onCloseRep } = useDisclosure();
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<Herramienta | null>(null);
  const [data, setData] = useState<Herramienta[]>([]);
  const [categorias, setCategorias] = useState<CatalogoItem[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await herramientasApi.list());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchData();
    catalogosApi.categoriasHerramienta().then(setCategorias).catch(() => setCategorias([]));
  }, []);

  const datosFiltrados = data.filter((item) => {
    const matchEstado = filtroEstado ? item.estadoFisico === filtroEstado : true;
    const matchCategoria = filtroCategoria ? String(item.categoriaId) === filtroCategoria : true;
    const q = busqueda.toLowerCase();
    const matchBusqueda = busqueda
      ? item.sku?.toLowerCase().includes(q) || item.nombre?.toLowerCase().includes(q) ||
        item.marca?.toLowerCase().includes(q) || item.responsable?.toLowerCase().includes(q)
      : true;
    return matchEstado && matchCategoria && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const abrirCon = async (item: Herramienta, open: () => void) => {
    try {
      setSelectedItem(await herramientasApi.get(item.id));
    } catch {
      setSelectedItem(item);
    }
    open();
  };

  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };

  const handleEliminarConfirmar = async () => {
    if (!selectedItem) return;
    setProcesando(true);
    try {
      await herramientasApi.remove(selectedItem.id);
      toast({ title: 'Herramienta eliminada', status: 'success', duration: 3000 });
      onCloseEliminar();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      SKU: item.sku,
      Nombre: item.nombre,
      Categoría: item.categoria?.nombre || '',
      Cantidad: item.cantidad,
      Disponible: item.cantidadDisponible,
      'Estado físico': item.estadoFisico,
      Ubicación: item.ubicacion,
      Responsable: item.responsable,
      Estatus: item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Herramientas');
    XLSX.writeFile(wb, `Herramientas_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const [generandoPdf, setGenerandoPdf] = useState(false);
  const handleReportePdf = async () => {
    setGenerandoPdf(true);
    try {
      const params: any = {};
      if (filtroEstado) params.estadoFisico = filtroEstado;
      if (filtroCategoria) params.categoriaId = filtroCategoria;
      const blob = await herramientasApi.reportePdf(params);
      abrirPdfEnNuevaPestana(blob);
    } catch {
      toast({ title: 'Error', description: 'No se pudo generar el reporte PDF.', status: 'error', duration: 4000 });
    } finally {
      setGenerandoPdf(false);
    }
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por SKU, nombre, marca..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '240px' }} />
          <Select placeholder="Categoría" value={filtroCategoria} onChange={(e) => { setFiltroCategoria(e.target.value); setCurrentPage(1); }} size="sm" w="170px">
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
          <Select placeholder="Estado físico" value={filtroEstado} onChange={(e) => { setFiltroEstado(e.target.value); setCurrentPage(1); }} size="sm" w="150px">
            {ESTADOS_FISICOS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<FiFileText />} colorScheme="purple" variant="outline" onClick={onOpenRep}>Reportes</Button>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<FiFileText />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleReportePdf} isLoading={generandoPdf}>Reporte PDF</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={onOpenNuevo}>Nueva herramienta</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">SKU</Th>
              <Th color="white" fontSize="xs">Nombre</Th>
              <Th color="white" fontSize="xs">Categoría</Th>
              <Th color="white" fontSize="xs">Cant.</Th>
              <Th color="white" fontSize="xs">Disp.</Th>
              <Th color="white" fontSize="xs">Estado</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.400">No se encontraron herramientas</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td fontSize="sm">{item.sku}</Td>
                  <Td fontSize="sm"><Text noOfLines={1} maxW="180px">{item.nombre}</Text></Td>
                  <Td fontSize="sm">{item.categoria?.nombre || '—'}</Td>
                  <Td fontSize="sm">{item.cantidad}</Td>
                  <Td fontSize="sm"><Text color={item.cantidadDisponible > 0 ? 'green.600' : 'red.600'} fontWeight="bold">{item.cantidadDisponible}</Text></Td>
                  <Td><Badge colorScheme={ESTADO_COLORS[item.estadoFisico || ''] || 'gray'} fontSize="2xs">{item.estadoFisico || '—'}</Badge></Td>
                  <Td><Badge colorScheme={item.estatus === 'activo' ? 'green' : 'gray'} fontSize="xs" borderRadius="full" px={2}>{item.estatus}</Badge></Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => abrirCon(item, onOpenVer)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => abrirCon(item, onOpenEditar)}>Editar</Button>
                      <Button size="xs" colorScheme="teal" variant="outline" onClick={() => { setSelectedItem(item); onOpenMov(); }}>Movimientos</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSelectedItem(item); onOpenEliminar(); }}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? '#5b182b' : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver */}
      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Detalle de la Herramienta</ModalHeader>
          <ModalBody py={5}>{selectedItem && <FormularioHerramienta modo="view" data={selectedItem} />}</ModalBody>
          <ModalFooter><Button onClick={onCloseVer}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Editar Herramienta</ModalHeader>
          <ModalBody py={5}>{selectedItem && <FormularioHerramienta modo="edit" data={selectedItem} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nuevo */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Nueva Herramienta</ModalHeader>
          <ModalBody py={5}><FormularioHerramienta modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} /></ModalBody>
        </ModalContent>
      </Modal>

      {/* Movimientos */}
      {selectedItem && <ModalMovimientos isOpen={isOpenMov} onClose={onCloseMov} herramienta={selectedItem} onCambio={fetchData} />}

      {/* Reportes */}
      <ModalReportes isOpen={isOpenRep} onClose={onCloseRep} />

      {/* Eliminar */}
      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="#5b182b">Eliminar herramienta</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar <strong>{selectedItem?.nombre}</strong> ({selectedItem?.sku})? Esta acción no se puede deshacer.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminarConfirmar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaHerramientas;
